import { Printer, SharedProps } from '@/types'
import { Head, router, usePage } from '@inertiajs/react'
import { Alert, Button, Card, Code, Group, List, Stack, Switch, Text, TextInput, Title } from '@mantine/core'
import { IconPlus } from '@tabler/icons-react'
import { ChangeEvent, FormEvent, useState } from 'react'

function PrinterConfigurationList({ url }: { url: string }) {
  const { current_organization } = usePage<SharedProps>().props

  return (
    <List size="sm" spacing={4}>
      <List.Item>
        URL : <Code>{url}</Code>
      </List.Item>
      <List.Item>
        Identifiant : <Code>{current_organization!.slug}</Code>
      </List.Item>
      <List.Item>
        Mot de passe : <Code>{current_organization!.cloudprnt_token}</Code>
      </List.Item>
      <List.Item>Intervalle de polling : 3-5 secondes</List.Item>
    </List>
  )
}

function PrinterCard({ printer }: { printer: Printer }) {
  const { current_organization } = usePage<SharedProps>().props
  const [name, setName] = useState(printer.name)
  const base = `/organizations/${current_organization!.slug}`
  const printerUrl = `${window.location.origin}/cloudprnt/${printer.id}`

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => setName(event.currentTarget.value)
  const handleRename = () => router.patch(`${base}/printers/${printer.id}`, { printer: { name } })
  const handleDelete = () => {
    if (confirm(`Supprimer l'imprimante « ${printer.name} » ? Elle ne recevra plus de tickets.`)) {
      router.delete(`${base}/printers/${printer.id}`)
    }
  }

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack gap="md">
        <Group align="flex-end" justify="space-between">
          <TextInput label="Nom" size="md" value={name} onChange={handleNameChange} />
          <Group gap="xs">
            <Button variant="light" onClick={handleRename} disabled={!name.trim() || name === printer.name}>
              Renommer
            </Button>
            <Button variant="subtle" color="red" onClick={handleDelete}>
              Supprimer
            </Button>
          </Group>
        </Group>

        <Alert color="blue" title="Configuration de cette imprimante" variant="light">
          <Text size="sm" mb="xs">
            Configurez cette imprimante Star TSP143IV avec les paramètres suivants. Elle ne récupérera que les tickets
            qui lui sont destinés.
          </Text>
          <PrinterConfigurationList url={printerUrl} />
        </Alert>
      </Stack>
    </Card>
  )
}

export default function PrintersIndex({ printers }: SharedProps & { printers: Printer[] }) {
  const { current_organization } = usePage<SharedProps>().props
  const [newPrinterName, setNewPrinterName] = useState('')
  const base = `/organizations/${current_organization!.slug}`
  const cloudprntEnabled = current_organization!.cloudprnt_enabled
  const genericUrl = `${window.location.origin}/cloudprnt`

  const handleCloudprntToggle = (event: ChangeEvent<HTMLInputElement>) =>
    router.patch(`${base}/settings`, { organization: { cloudprnt_enabled: event.currentTarget.checked } })

  const handleNewPrinterNameChange = (event: ChangeEvent<HTMLInputElement>) =>
    setNewPrinterName(event.currentTarget.value)

  const handleAddPrinter = (event: FormEvent) => {
    event.preventDefault()
    router.post(`${base}/printers`, { printer: { name: newPrinterName } }, { onSuccess: () => setNewPrinterName('') })
  }

  return (
    <>
      <Head title={`Imprimantes - ${current_organization!.name}`} />

      <Stack maw={640} mx="auto" w="100%">
        <Title ta="center" order={2}>
          Imprimantes
        </Title>

        <Card withBorder radius="lg" padding="xl" shadow="sm">
          <Switch
            label="Imprimante CloudPRNT"
            description="L'imprimante récupère les tickets automatiquement via le serveur. Si désactivé, l'impression passe par la boîte de dialogue du navigateur."
            size="md"
            checked={cloudprntEnabled}
            onChange={handleCloudprntToggle}
          />
        </Card>

        {cloudprntEnabled && (
          <>
            {printers.length === 0 && (
              <Alert color="blue" title="Configuration de l'imprimante" variant="light">
                <Text size="sm" mb="xs">
                  Configurez votre imprimante Star TSP143IV avec les paramètres suivants :
                </Text>
                <PrinterConfigurationList url={genericUrl} />
                <Text size="sm" mt="xs">
                  Ajoutez des imprimantes ci-dessous pour en utiliser plusieurs : chacune aura sa propre URL et sa
                  propre file d'attente.
                </Text>
              </Alert>
            )}

            {printers.map((printer) => (
              <PrinterCard key={printer.id} printer={printer} />
            ))}

            <Card withBorder radius="md" padding="lg">
              <form onSubmit={handleAddPrinter}>
                <Group align="flex-end">
                  <TextInput
                    label="Ajouter une imprimante"
                    placeholder="Comptoir, Atelier…"
                    size="md"
                    style={{ flex: 1 }}
                    value={newPrinterName}
                    onChange={handleNewPrinterNameChange}
                  />
                  <Button type="submit" leftSection={<IconPlus size={16} />} disabled={!newPrinterName.trim()}>
                    Ajouter
                  </Button>
                </Group>
              </form>
            </Card>
          </>
        )}
      </Stack>
    </>
  )
}
